import { normalizeInternalRouteTarget } from '@/constants/security';
import { isSensitivePrivateRoute } from '@/lib/postAuthRouting';

const AUTH_BOUNDARY_PREFIXES = [
  '/login',
  '/register',
  '/request-password-reset',
  '/update-password',
  '/auth/callback',
  '/reset-password',
] as const;

const LEGACY_EXTERNAL_AUTH_PREFIXES = [
  '/propietario/dashboard',
  '/inquilino/dashboard',
  '/proveedor/dashboard',
  '/owner',
] as const;

const SCRUBBED_AUTH_PARAMS = [
  'code',
  'state',
  'error',
  'error_code',
  'error_description',
  'next',
  'post',
  'redirect',
  'intent',
  'access_token',
  'refresh_token',
  'expires_at',
  'expires_in',
  'token_type',
  'type',
  'token_hash',
] as const;

export const PASSWORD_RECOVERY_READY_STORAGE_KEY = 'rc:passwordRecoveryReady';
export const PUBLIC_AUTH_COMPLETION_PROMPT_TRIGGER_KEY = 'rc:publicAuthCompletionPromptTrigger';

type PublicAuthCompletionPromptState = 'dismissed' | 'completed';

type PublicAuthCompletionPromptSnapshot = {
  status: PublicAuthCompletionPromptState;
  updatedAt: string;
};

function getPublicAuthCompletionPromptStateKey(userId: string): string {
  return `rc:publicAuthCompletionPromptState:${userId}`;
}

function getPublicAuthCompletionPromptSessionSeenKey(userId: string): string {
  return `rc:publicAuthCompletionPromptSessionSeen:${userId}`;
}

type RedirectPolicy = {
  allowInternalRoutes?: boolean;
  fallback?: string;
};

function decodeCandidate(value: string): string {
  let decoded = value;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      break;
    }
  }

  return decoded;
}

function normalizePathCandidate(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') {
    return null;
  }

  const trimmed = decodeCandidate(raw.trim());
  if (!trimmed || trimmed.startsWith('//')) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  }

  if (!trimmed.startsWith('/')) {
    return null;
  }

  try {
    const parsed = new URL(trimmed, 'https://public-auth.local');
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return trimmed;
  }
}

function matchesPathPrefix(path: string, prefix: string): boolean {
  return (
    path === prefix
    || path.startsWith(`${prefix}/`)
    || path.startsWith(`${prefix}?`)
    || path.startsWith(`${prefix}#`)
  );
}

function isAuthBoundaryPath(path: string): boolean {
  return AUTH_BOUNDARY_PREFIXES.some((prefix) => matchesPathPrefix(path, prefix));
}

function isLegacyExternalAuthPath(path: string): boolean {
  return LEGACY_EXTERNAL_AUTH_PREFIXES.some((prefix) => matchesPathPrefix(path, prefix));
}

function sanitizeWithPolicy(raw: string | null | undefined, policy: RedirectPolicy = {}): string {
  const { allowInternalRoutes = false, fallback = '/' } = policy;
  const normalized = normalizePathCandidate(raw);

  if (!normalized || isAuthBoundaryPath(normalized) || isLegacyExternalAuthPath(normalized)) {
    return fallback;
  }

  if (!allowInternalRoutes && isSensitivePrivateRoute(normalized)) {
    return fallback;
  }

  if (allowInternalRoutes && isSensitivePrivateRoute(normalized)) {
    return normalizeInternalRouteTarget(normalized) ?? fallback;
  }

  return normalized;
}

export function normalizePublicAuthEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidPublicAuthEmail(raw: string): boolean {
  const normalized = normalizePublicAuthEmail(raw);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

export function sanitizeCanonicalPublicDestination(raw: string | null | undefined, fallback = '/'): string {
  return sanitizeWithPolicy(raw, { allowInternalRoutes: false, fallback });
}

export function sanitizeCanonicalLoginRedirect(raw: string | null | undefined, fallback = '/'): string {
  return sanitizeWithPolicy(raw, { allowInternalRoutes: true, fallback });
}

export function readAuthFlowDestinationParam(
  paramNames: string[],
  options: RedirectPolicy = {},
): string {
  if (typeof window === 'undefined') {
    return options.fallback ?? '/';
  }

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));

  for (const name of paramNames) {
    const raw = searchParams.get(name) ?? hashParams.get(name);
    if (raw) {
      return sanitizeWithPolicy(raw, options);
    }
  }

  return options.fallback ?? '/';
}

export function persistAuthIntent(
  key: string,
  raw: string | null | undefined,
  options: RedirectPolicy = {},
): string {
  const sanitized = sanitizeWithPolicy(raw, options);

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(key, sanitized);
    } catch {
      // noop
    }
  }

  return sanitized;
}

export function readStoredAuthIntent(keys: string[], options: RedirectPolicy = {}): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      return sanitizeWithPolicy(raw, options);
    } catch {
      // noop
    }
  }

  return null;
}

export function clearStoredAuthIntent(keys: string[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  keys.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // noop
    }
  });
}

export function hasRecoveryFlowSignals(locationLike: Pick<Location, 'search' | 'hash'> = window.location): boolean {
  const searchParams = new URLSearchParams(locationLike.search);
  const hashParams = new URLSearchParams(locationLike.hash.replace(/^#/, ''));
  const flowType = searchParams.get('type') ?? hashParams.get('type');

  return (
    flowType === 'recovery'
    || searchParams.has('code')
    || searchParams.has('token_hash')
    || hashParams.has('access_token')
    || hashParams.has('refresh_token')
    || hashParams.has('token_hash')
  );
}

export function scrubAuthFlowUrl(currentUrl = window.location.href): string {
  const parsed = new URL(currentUrl);

  SCRUBBED_AUTH_PARAMS.forEach((key) => {
    parsed.searchParams.delete(key);
  });

  const hashParams = new URLSearchParams(parsed.hash.replace(/^#/, ''));
  SCRUBBED_AUTH_PARAMS.forEach((key) => {
    hashParams.delete(key);
  });

  const nextHash = hashParams.toString();
  parsed.hash = nextHash ? `#${nextHash}` : '';

  return parsed.toString();
}

export function markPublicAuthCompletionPromptTrigger(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(PUBLIC_AUTH_COMPLETION_PROMPT_TRIGGER_KEY, '1');
  } catch {
    // noop
  }
}

export function hasPublicAuthCompletionPromptTrigger(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(PUBLIC_AUTH_COMPLETION_PROMPT_TRIGGER_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearPublicAuthCompletionPromptTrigger(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(PUBLIC_AUTH_COMPLETION_PROMPT_TRIGGER_KEY);
  } catch {
    // noop
  }
}

export function readPublicAuthCompletionPromptState(userId: string | null | undefined): PublicAuthCompletionPromptState | null {
  if (typeof window === 'undefined' || !userId) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getPublicAuthCompletionPromptStateKey(userId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PublicAuthCompletionPromptSnapshot>;
    if (parsed.status === 'dismissed' || parsed.status === 'completed') {
      return parsed.status;
    }
  } catch {
    // noop
  }

  return null;
}

export function writePublicAuthCompletionPromptState(
  userId: string | null | undefined,
  status: PublicAuthCompletionPromptState,
): void {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  const snapshot: PublicAuthCompletionPromptSnapshot = {
    status,
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(getPublicAuthCompletionPromptStateKey(userId), JSON.stringify(snapshot));
  } catch {
    // noop
  }
}

export function hasPublicAuthCompletionPromptSessionSeen(userId: string | null | undefined): boolean {
  if (typeof window === 'undefined' || !userId) {
    return false;
  }

  try {
    return window.sessionStorage.getItem(getPublicAuthCompletionPromptSessionSeenKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markPublicAuthCompletionPromptSessionSeen(userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  try {
    window.sessionStorage.setItem(getPublicAuthCompletionPromptSessionSeenKey(userId), '1');
  } catch {
    // noop
  }
}